import React from '../../vendor/react';
import {
  ArrowUpDown,
  BarChart3,
  Boxes,
  Building2,
  ChevronDown,
  CircleDollarSign,
  ClipboardList,
  Construction,
  ContactRound,
  History,
  LayoutDashboard,
  PackageSearch,
  Plus,
  QrCode,
  Repeat,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Store,
  Truck,
  UserRound,
  Wifi,
  X,
} from '../../vendor/lucide-react';
import { Link, NavLink, useLocation } from '../../vendor/react-router-dom';
import { BrandMark } from './BrandMark';
import { cn } from '../../lib/cn';
import { useWorkspace } from '../../features/workspace/useWorkspace';
import { useSaas } from '../../features/saas/useSaas';
import type { CommercialFeatureId } from '../../config/commercialPlans';

type NavigationItem = {
  to: string;
  label: string;
  icon: (props: { size?: number }) => JSX.Element;
  end?: boolean;
  feature?: CommercialFeatureId;
};

const mainLinks: NavigationItem[] = [
  { to: '/app', label: 'Resumen', icon: LayoutDashboard, end: true },
  { to: '/app/pos', label: 'Punto de venta', icon: ShoppingCart },
  { to: '/app/ventas', label: 'Ventas', icon: CircleDollarSign },
];

const navigationGroups: Array<{ label: string; items: NavigationItem[] }> = [
  {
    label: 'Catálogo y pedidos',
    items: [
      { to: '/app/productos', label: 'Productos', icon: Boxes },
      { to: '/app/inventario', label: 'Inventario', icon: PackageSearch },
      { to: '/app/pedidos', label: 'Pedidos', icon: ClipboardList, feature: 'catalogOrders' },
      { to: '/app/pedidos-especiales', label: 'Pedidos especiales', icon: Construction, feature: 'specialOrders' },
      { to: '/app/transferencias', label: 'Transferencias', icon: ArrowUpDown, feature: 'transfers' },
    ],
  },
  {
    label: 'Contactos y compras',
    items: [
      { to: '/app/clientes', label: 'Clientes', icon: ContactRound },
      { to: '/app/clientes-en-tienda', label: 'Clientes en tienda', icon: QrCode, feature: 'customerCheckIn' },
      { to: '/app/proveedores', label: 'Proveedores', icon: Truck },
      { to: '/app/compras', label: 'Compras', icon: ClipboardList },
      { to: '/app/gastos', label: 'Gastos', icon: Plus },
      { to: '/app/membresias', label: 'Membresías', icon: Repeat, feature: 'memberships' },
      { to: '/app/cobranza', label: 'Crédito y cobranza', icon: CircleDollarSign },
    ],
  },
  {
    label: 'Control y análisis',
    items: [
      { to: '/app/caja', label: 'Caja y cortes', icon: CircleDollarSign },
      { to: '/app/cierres', label: 'Cierres y periodos', icon: History },
      { to: '/app/reportes', label: 'Reportes', icon: BarChart3 },
      { to: '/app/nexo-ia', label: 'NEXO IA', icon: Sparkles },
      { to: '/app/auditoria', label: 'Auditoría', icon: History },
      { to: '/app/sincronizacion', label: 'Sincronización', icon: Wifi },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { to: '/app/plan', label: 'Plan y licencia', icon: ShieldCheck },
      { to: '/app/configuracion', label: 'Ajustes', icon: Settings },
      { to: '/app/cuenta', label: 'Mi cuenta', icon: UserRound },
    ],
  },
];

function SidebarLink({ item, onClose }: { item: NavigationItem; onClose: () => void }) {
  const Icon = item.icon;
  return <NavLink to={item.to} end={item.end} onClick={onClose} className={({ isActive }) => cn('sidebar-link', isActive && 'is-active')}><Icon size={18} /><span>{item.label}</span></NavLink>;
}

export function AppSidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const workspace = useWorkspace();
  const saas = useSaas();
  const location = useLocation();
  const allowed = (item: NavigationItem) => !item.feature || saas.hasFeature(item.feature);
  return (
    <>
      <button className={cn('sidebar-scrim', open && 'is-open')} aria-label="Cerrar menú" onClick={onClose} />
      <aside className={cn('app-sidebar', open && 'is-open')}>
        <div className="app-sidebar__brand"><BrandMark /><button className="icon-button sidebar-close" aria-label="Cerrar menú" onClick={onClose}><X size={20} /></button></div>
        <Link to="/app/seleccionar-contexto" className="business-switcher" onClick={onClose}>
          <span className="business-switcher__icon" style={{ background: workspace.business?.primaryColor ? `color-mix(in srgb, ${workspace.business.primaryColor} 16%, transparent)` : undefined }}><Store size={18} /></span>
          <span><strong>{workspace.business?.name ?? 'Selecciona un negocio'}</strong><small>{workspace.branch?.name ?? 'Sin sucursal'}</small></span><ChevronDown size={16} />
        </Link>
        <nav className="sidebar-nav" aria-label="Navegación principal">
          <p className="sidebar-label">Principal</p>
          {mainLinks.filter(allowed).map((item) => <SidebarLink key={item.to} item={item} onClose={onClose} />)}
          <div className="sidebar-groups">
            {navigationGroups.map((group) => {
              const items = group.items.filter(allowed);
              const active = items.some((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));
              return <details className="sidebar-group" key={group.label} open={active}>
                <summary><span>{group.label}</span><ChevronDown size={15} /></summary>
                <div>{items.map((item) => <SidebarLink key={item.to} item={item} onClose={onClose} />)}</div>
              </details>;
            })}
          </div>
        </nav>
        <div className="sidebar-footer"><Building2 size={18} /><div><strong>{workspace.roleLabel}</strong><span>Acceso a {workspace.business?.name ?? 'tu negocio'}</span></div></div>
      </aside>
    </>
  );
}
